'use client';

import { FC, useState } from 'react';

import { ShoppingCartItem } from '@ravago/shared/page-data/models/features';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Breadcrumbs } from '@ravago/shared/radiance/components/breadcrumbs/components/Breadcrumbs/Breadcrumbs';
import { BreakpointSwitch } from '@ravago/shared/radiance/components/breakpoint-switch/components/BreakpointSwitch/BreakpointSwitch';
import { Button } from '@ravago/shared/radiance/components/button/components/Button/Button';
import { Grid } from '@ravago/shared/radiance/components/grid/components/Grid/Grid';
import { GridCol } from '@ravago/shared/radiance/components/grid/components/GridCol/GridCol';
import { Icon } from '@ravago/shared/radiance/components/icon/components/Icon/Icon';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useTranslations } from '@ravago/shared/translations/client';

import OrderNotFoundIllustration from '../../../illustrations/order-not-found-illustration';
import { EmptyState } from '../../../shared/empty-state/empty-state.component';
import { Link } from '../../../shared/link/link';

import { ShoppingCartItemElement } from './shopping-cart-item-element';
import { ShoppingCartOrderSummary } from './shopping-cart-order-summary';

interface Props {
   initialShoppingCartItems: Array<ShoppingCartItem>;
   removeFromShoppingCart: (shoppingCartItemId: ShoppingCartItem['id']) => Promise<ShoppingCartItem[]>;
   languageCode: string;
   locale: string;
}

export const ShoppingCartElement: FC<Props> = ({ initialShoppingCartItems, removeFromShoppingCart, languageCode, locale }) => {
   const translate = useTranslations();
   const [shoppingCartItems, setShoppingCartItems] = useState<Array<ShoppingCartItem>>(initialShoppingCartItems);

   const handleRemoveFromShoppingCart = async (shoppingCartItemId: ShoppingCartItem['id']) => {
      setShoppingCartItems(await removeFromShoppingCart(shoppingCartItemId));
   };

   if (shoppingCartItems.length === 0) {
      return (
         <Grid>
            <GridCol spanDesktop={6} spanTablet={6} spanPhone={4} startDesktop={4} startTablet={2} startPhone={1}>
               <Box direction="column" items="center">
                  <EmptyState
                     image={<OrderNotFoundIllustration />}
                     title={translate('shopping-cart.empty-title')}
                     content={translate('shopping-cart.empty-description')}
                  />

                  <Link underline="none" color="light" href={`/my/${languageCode}/my-prices`} prefetch={false}>
                     <Button> {translate('shopping-cart.my-prices')}</Button>
                  </Link>
               </Box>
            </GridCol>
         </Grid>
      );
   }

   const cartItems = shoppingCartItems.map((shoppingCartItem: ShoppingCartItem) => (
      <ShoppingCartItemElement
         locale={locale}
         key={shoppingCartItem.id}
         data-testid="shopping-cart__shopping-cart-item"
         removeFromShoppingCart={handleRemoveFromShoppingCart}
         shoppingCartItem={shoppingCartItem}
      />
   ));

   return (
      <Box direction="column">
         <Breadcrumbs>
            <Link href={`/${languageCode}`} prefetch={false}>
               <Icon icon="home" />
            </Link>
            {translate('shopping-cart.header-shopping-cart')}
         </Breadcrumbs>

         <Typography component="h1" variant="heading-2">
            {translate('shopping-cart.header-shopping-cart')}
         </Typography>

         <Typography variant="caption">
            {shoppingCartItems.length}{' '}
            {shoppingCartItems.length > 1 ? translate('shopping-cart.items-count.plural') : translate('shopping-cart.items-count.single')}
         </Typography>

         <Grid>
            <GridCol spanDesktop={8} spanTablet={8} spanPhone={4}>
               <Box direction="column" gap="sm">
                  {cartItems}
               </Box>
            </GridCol>
            <BreakpointSwitch excludeViews={['phone', 'tab-port']}>
               <GridCol spanDesktop={4}>
                  <ShoppingCartOrderSummary languageCode={languageCode} shoppingCartItems={shoppingCartItems} />
               </GridCol>
            </BreakpointSwitch>
         </Grid>

         <BreakpointSwitch includeViews={['phone', 'tab-port']}>
            <Box position="fixed" zIndex="39" width="90%" bottom="0" direction="column" gap="2xs">
               <ShoppingCartOrderSummary languageCode={languageCode} shoppingCartItems={shoppingCartItems} />
            </Box>
         </BreakpointSwitch>
      </Box>
   );
};
